import React, { useMemo } from 'react'
import { graphql, useFragment } from 'react-relay'

import { XStack, YStack, useMedia } from 'tamagui'

import { lazy as loadable } from '@loadable/component'

import {
  Paragraph,
  Icon,
  Text,
  Card,
  EventAttendButton,
  EventAttendeesOverlap,
  Dialog,
  wrapSuspenseComponent,
} from '@newComponents'

import { differenceInCalendarDays } from 'date-fns'

import {
  EventScreenAttendCard_event$key,
  EventScreenAttendCard_event,
} from './__generated__/EventScreenAttendCard_event.graphql'
import { EventScreenAttendCard_query$key } from './__generated__/EventScreenAttendCard_query.graphql'

const EventScreenAttendeesDialog = wrapSuspenseComponent(
  loadable(() => import('./EventScreenAttendeesDialog'))
)

export const EventScreenAttendCard = ({
  event,
  query,
}: {
  event: EventScreenAttendCard_event$key
  query: EventScreenAttendCard_query$key
}) => {
  const data = useFragment(
    graphql`
      fragment EventScreenAttendCard_event on Event {
        slug
        startAt
        endAt

        eventAttendeesByRelevance(
          filter: {
            status: {
              in: [ATTENDING_IN_PERSON, ATTENDING_VIRTUAL, WAITING_IN_PERSON]
            }
          }
          first: 5
        ) {
          totalCount
          ...EventAttendeesOverlap_eventAttendees
        }

        attendingCount: eventAttendees(
          filter: { status: { in: [ATTENDING_IN_PERSON, ATTENDING_VIRTUAL] } }
        ) {
          totalCount
        }

        waitingCount: eventAttendees(condition: { status: WAITING_IN_PERSON }) {
          totalCount
        }

        myAttendance {
          status
        }

        ...EventAttendButtonNew_event
      }
    `,
    event
  )

  const queryData = useFragment(
    graphql`
      fragment EventScreenAttendCard_query on Query {
        ...EventAttendButtonNew_query
      }
    `,
    query
  )

  return (
    <Card gap='$4'>
      <EventScreenAttendCardHeader data={data} />

      <XStack
        gap='$4'
        paddingTop='$1'
        display='none'
        $gtLg={{ display: 'flex' }}
      >
        <EventScreenAttendeesDialogTrigger data={data} />

        <EventAttendButton event={data} query={queryData} />
      </XStack>

      <YStack gap='$4' display='flex' $gtLg={{ display: 'none' }}>
        <EventScreenAttendeesDialogTrigger data={data} />

        <YStack alignItems='flex-end' justifyContent='flex-end'>
          <EventAttendButton event={data} query={queryData} />
        </YStack>
      </YStack>
    </Card>
  )
}

const EventScreenAttendCardHeader = ({
  data,
}: {
  data: EventScreenAttendCard_event
}) => {
  const daysToGo = useMemo(() => {
    return differenceInCalendarDays(new Date(data.startAt), new Date())
  }, [data.startAt])

  const eventInPast = useMemo(() => {
    return new Date().getTime() > new Date(data.endAt).getTime()
  }, [data.endAt])

  if (!data.myAttendance || data.myAttendance.status === 'NOT_ATTENDING') {
    if (eventInPast) {
      return <Paragraph size='$4'>We missed you this time around!</Paragraph>
    }

    return (
      <>
        {data.attendingCount.totalCount > 5 ? (
          <Paragraph size='$4'>
            👋 Ready to join {data.attendingCount.totalCount} other attendees?
          </Paragraph>
        ) : (
          <Paragraph size='$4'>👋 Ready to join in on the fun?</Paragraph>
        )}

        {data.myAttendance?.status === 'NOT_ATTENDING' && (
          <Paragraph size='$4'>
            You're not attending, and won't receive notifications for this
            event.
          </Paragraph>
        )}
      </>
    )
  }

  if (data.myAttendance.status === 'WAITING_IN_PERSON') {
    if (eventInPast) {
      return <Paragraph size='$4'>We missed you this time around!</Paragraph>
    }

    return (
      <>
        <Paragraph size='$4'>
          You're waiting for a spot to become available.
        </Paragraph>

        <Paragraph size='$4'>We'll email you if you get one!</Paragraph>
      </>
    )
  }

  if (daysToGo > 0) {
    return (
      <>
        <Paragraph size='$4'>You're attending! 🎉</Paragraph>

        <Paragraph size='$4'>
          Only {daysToGo} more {daysToGo === 1 ? 'day' : 'days'} to go 🙌
        </Paragraph>
      </>
    )
  }

  if (daysToGo === 0) {
    return (
      <>
        <Paragraph size='$4'>You're attending! 🎉</Paragraph>

        <Paragraph size='$4'>Today is the day 🙌</Paragraph>
      </>
    )
  }

  return (
    <>
      <Paragraph size='$4'>You attended! 🎉</Paragraph>

      <Paragraph size='$4'>
        We look forward to seeing you next time 👋
      </Paragraph>
    </>
  )
}

const EventScreenAttendeesDialogTrigger = ({
  data,
}: {
  data: EventScreenAttendCard_event
}) => {
  const media = useMedia()

  const limitAttendees = useMemo(() => {
    if (media.gtMd) {
      return 5
    }

    if (media.gtSm) {
      return 4
    }
  }, [media])

  return (
    <Dialog modal>
      <Dialog.Trigger asChild>
        <XStack
          flex={1}
          gap='$4'
          alignItems='center'
          cursor='pointer'
          onHoverIn={() => {
            // TODO: Swap attendee text with hover instructions
          }}
          onHoverOut={() => {
            // TODO: Swap attendee text with hover instructions
          }}
        >
          {!!data.eventAttendeesByRelevance.totalCount && (
            <>
              <EventAttendeesOverlap
                size='$7'
                eventAttendees={data.eventAttendeesByRelevance}
                limitAttendees={limitAttendees}
              />

              <YStack>
                <Paragraph size='$4'>
                  {data.attendingCount.totalCount} attending
                </Paragraph>

                {!!data.waitingCount.totalCount && (
                  <Paragraph size='$4'>
                    {data.waitingCount.totalCount} waiting
                  </Paragraph>
                )}
              </YStack>
            </>
          )}
        </XStack>
      </Dialog.Trigger>

      <Dialog.Adapt when='xs'>
        <Dialog.Sheet key='sheet'>
          <Dialog.Sheet.Handle zIndex={200_000} />
          <Dialog.Sheet.Frame
            padding={0}
            gap='$4'
            shadowColor='$grey6'
            shadowRadius='$2'
            borderTopRightRadius='$6'
            borderTopLeftRadius='$6'
          >
            <Dialog.Sheet.ScrollView
              borderTopRightRadius='$6'
              borderTopLeftRadius='$6'
              padding='$6'
            >
              <YStack gap='$4'>
                <Dialog.Adapt.Contents />
              </YStack>
            </Dialog.Sheet.ScrollView>
          </Dialog.Sheet.Frame>
        </Dialog.Sheet>
      </Dialog.Adapt>

      <Dialog.Portal>
        <Dialog.Content
          elevate
          key='content'
          gap='$4'
          minWidth={400}
          maxWidth={600}
          minHeight={400}
          maxHeight={600}
        >
          <EventScreenAttendeesDialog slug={data.slug} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  )
}
